import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { SCREENS, NAV_GROUPS } from "../lib/constants";
import { useI18n } from "../lib/i18n";

function Metric({ label, value, color }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-edge raise">
      <span className="text-[10px] uppercase tracking-overline text-slate-400">{label}</span>
      <span className={`font-mono text-sm tabular ${color}`}>{value}</span>
    </div>
  );
}

export default function Sidebar({ screen, setScreen, status, exceptionCount }) {
  const { t } = useI18n();
  const s = status || {};
  return (
    <aside className="no-print hidden lg:flex w-[268px] shrink-0 flex-col border-r border-edge bg-panel/60 h-full overflow-y-auto">
      <nav className="flex flex-col gap-1 p-4">
        {NAV_GROUPS.map((g) => (
          <div key={g.id} className="mb-2">
            <p className="px-3 pb-1.5 pt-2 text-[10px] uppercase tracking-overline text-slate-400">{t(g.key)}</p>
            {SCREENS.filter((sc) => sc.group === g.id).map((item) => {
              const Icon = item.icon;
              const active = screen === item.id;
              return (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  onClick={() => setScreen(item.id)}
                  className={`group relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors duration-200 ${
                    active ? "bg-aegis-cyan/10 text-slate-100" : "text-slate-400 hover:text-slate-200 hov-raise"
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-aegis-cyan"
                    />
                  )}
                  <span
                    className={`font-mono text-[10px] w-4 shrink-0 ${active ? "text-aegis-cyan" : "text-slate-400"}`}
                  >
                    {item.num}
                  </span>
                  <Icon
                    size={17}
                    strokeWidth={1.6}
                    className={active ? "text-aegis-cyan" : "text-slate-400 group-hover:text-slate-300"}
                  />
                  <span className="text-[13px] font-medium truncate">{t(item.key)}</span>
                  {item.id === "exception" && exceptionCount > 0 && (
                    <span className="ml-auto rounded-full bg-aegis-red/15 border border-aegis-red/40 px-1.5 text-[10px] font-mono text-aegis-red">
                      {exceptionCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="mt-auto p-4 space-y-2">
        <Metric label={t("exec.kpi.blocked")} value={s.blocked_actions ?? "—"} color="text-aegis-red" />
        <Metric label={t("exec.kpi.pending")} value={s.pending_count ?? "—"} color="text-aegis-amber" />
        <Metric label={t("exec.kpi.height")} value={`#${s.block_height ?? "—"}`} color="text-aegis-emerald" />
        <div className="flex items-center gap-2 px-3 pt-3 text-[11px] text-slate-400">
          <ShieldCheck size={14} className="text-aegis-emerald" />
          <span className="truncate">{s.sandbox || "No-Tool Sandbox Active"}</span>
        </div>
      </div>
    </aside>
  );
}
